import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Upload, Trash2, FileText, Image as ImageIcon, Film, RefreshCw, ExternalLink, Check, RotateCcw, Download, Settings, Mail, History, Undo2, ShieldAlert } from "lucide-react";
import { api, resolveMedia } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { DEFAULTS, MEDIA_DEFAULTS } from "@/content/defaults";
import { IMAGE_SLOTS, VIDEO_SLOTS, PAGE_LABELS, FIELD_LABELS, prettifyKey } from "@/pages/adminConfig";

const clone = (o) => JSON.parse(JSON.stringify(o));

const SEC_EVENT_META = {
  AUTH_RATE_LIMIT: { label: "Trop de tentatives de connexion", tone: "red" },
  AUTH_INVALID_SESSION_ID: { label: "Session OAuth invalide", tone: "amber" },
  AUTH_BAD_SETUP_CODE: { label: "Code de configuration incorrect", tone: "red" },
  AUTH_DENIED_NON_ADMIN: { label: "Connexion refusée — email non autorisé", tone: "red" },
  ADMIN_CLAIMED: { label: "Compte admin revendiqué", tone: "blue" },
  FORM_HONEYPOT: { label: "Spam bloqué (honeypot)", tone: "neutral" },
  FORM_IP_RATE_LIMIT: { label: "Limite formulaire par IP atteinte", tone: "amber" },
  FORM_GLOBAL_RATE_LIMIT: { label: "Limite globale de formulaire atteinte", tone: "amber" },
};
const SEC_TONE = {
  red: "bg-red-50 text-red-600",
  amber: "bg-amber-50 text-amber-700",
  blue: "bg-[#1CADE4]/12 text-[#1CADE4]",
  neutral: "bg-neutral-100 text-neutral-500",
};

// Save ONLY fields that differ from code defaults, so untouched fields always
// reflect the latest defaults (prevents accidental reversions of locked snapshots).
const diffContent = (text, defaults) => {
  const out = {};
  for (const page of Object.keys(defaults)) {
    const pageDiff = {};
    for (const key of Object.keys(defaults[page])) {
      const def = defaults[page][key] || {};
      const cur = (text[page] && text[page][key]) || {};
      if ((cur.fr ?? "") !== (def.fr ?? "") || (cur.en ?? "") !== (def.en ?? "")) {
        pageDiff[key] = { fr: cur.fr ?? "", en: cur.en ?? "" };
      }
    }
    if (Object.keys(pageDiff).length) out[page] = pageDiff;
  }
  return out;
};

const diffMedia = (mediaMap, defaults) => {
  const out = {};
  for (const slot of Object.keys(mediaMap)) {
    if ((mediaMap[slot] || "") !== (defaults[slot] || "")) out[slot] = mediaMap[slot];
  }
  return out;
};


export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { content, media, refreshContent } = useApp();
  const [user, setUser] = useState(location.state?.user || null);
  const [authChecked, setAuthChecked] = useState(!!location.state?.user);
  const [tab, setTab] = useState("content");

  const [text, setText] = useState(() => {
    const t = clone(DEFAULTS);
    return t;
  });
  const [mediaMap, setMediaMap] = useState(() => clone(MEDIA_DEFAULTS));
  const [library, setLibrary] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingAlert, setSavingAlert] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [snapBusy, setSnapBusy] = useState(false);
  const [securityEvents, setSecurityEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openPage, setOpenPage] = useState("home");
  const fileRef = useRef(null);
  const importRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        navigate("/admin", { replace: true });
        return;
      } finally {
        setAuthChecked(true);
      }
    };
    if (!location.state?.user) check();
  }, [navigate, location.state]);

  // hydrate editable state from live content once loaded
  useEffect(() => {
    setText(clone(content));
    setMediaMap({ ...clone(MEDIA_DEFAULTS), ...clone(media) });
  }, [content, media]);

  const loadLibrary = async () => {
    try { const { data } = await api.get("/media"); setLibrary(data); } catch {}
  };
  const loadSubmissions = async () => {
    try { const { data } = await api.get("/submissions"); setSubmissions(data); } catch {}
  };
  const loadSettings = async () => {
    try { const { data } = await api.get("/settings"); setRecipientEmail(data.form_recipient_email || ""); setAlertEmail(data.security_alert_email || ""); } catch {}
  };
  const loadSnapshots = async () => {
    try { const { data } = await api.get("/snapshots"); setSnapshots(data); } catch {}
  };
  const loadSecurityEvents = async () => {
    try { const { data } = await api.get("/security-events"); setSecurityEvents(data); } catch {}
  };
  useEffect(() => { if (authChecked) { loadLibrary(); loadSubmissions(); loadSettings(); loadSnapshots(); loadSecurityEvents(); } }, [authChecked]);

  const createSnapshot = async () => {
    setSnapBusy(true);
    try {
      await api.post("/snapshots", { label: snapshotLabel.trim() });
      setSnapshotLabel("");
      await loadSnapshots();
      toast.success("Sauvegarde créée.");
    } catch { toast.error("Erreur lors de la création de la sauvegarde."); }
    finally { setSnapBusy(false); }
  };

  const restoreSnapshot = async (snap) => {
    const when = snap.created_at ? new Date(snap.created_at).toLocaleString() : "";
    if (!window.confirm(`Restaurer cette version du ${when} ? Le contenu actuel sera d'abord sauvegardé automatiquement, puis remplacé par cette version.`)) return;
    setSnapBusy(true);
    try {
      await api.post(`/snapshots/${snap.id}/restore`);
      await refreshContent();
      await loadSnapshots();
      toast.success("Version restaurée.");
    } catch { toast.error("Erreur lors de la restauration."); }
    finally { setSnapBusy(false); }
  };

  const deleteSnapshot = async (id) => {
    if (!window.confirm("Supprimer définitivement cette sauvegarde ?")) return;
    try { await api.delete(`/snapshots/${id}`); await loadSnapshots(); toast.success("Sauvegarde supprimée."); }
    catch { toast.error("Erreur de suppression."); }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/settings", { form_recipient_email: recipientEmail.trim() });
      toast.success("Email de réception mis à jour.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Email invalide.");
    } finally {
      setSavingSettings(false);
    }
  };

  const saveAlertEmail = async () => {
    setSavingAlert(true);
    try {
      await api.put("/settings", { security_alert_email: alertEmail.trim() });
      toast.success("Email d'alerte de sécurité mis à jour.");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Email invalide.");
    } finally {
      setSavingAlert(false);
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    navigate("/admin", { replace: true });
  };

  const setField = (page, key, lang, value) =>
    setText((prev) => ({ ...prev, [page]: { ...prev[page], [key]: { ...prev[page][key], [lang]: value } } }));

  const isModified = (page, key) => {
    const def = DEFAULTS[page][key] || {};
    const cur = (text[page] && text[page][key]) || {};
    return (cur.fr ?? "") !== (def.fr ?? "") || (cur.en ?? "") !== (def.en ?? "");
  };

  const resetField = (page, key) =>
    setText((prev) => ({ ...prev, [page]: { ...prev[page], [key]: { ...DEFAULTS[page][key] } } }));

  const pageModifiedCount = (page) =>
    Object.keys(DEFAULTS[page]).filter((key) => isModified(page, key)).length;

  const resetPage = (page) => {
    const count = pageModifiedCount(page);
    if (count === 0) return;
    if (!window.confirm(`Réinitialiser les ${count} champ(s) modifié(s) de « ${PAGE_LABELS[page]} » au texte par défaut ? Cette action remplacera votre texte personnalisé sur cette page (effectif après « Enregistrer »).`)) return;
    setText((prev) => ({ ...prev, [page]: clone(DEFAULTS[page]) }));
    toast.success(`« ${PAGE_LABELS[page]} » réinitialisée. Cliquez sur Enregistrer pour appliquer.`);
  };

  const exportContent = () => {
    const payload = { site: "Agrivoire Export SARL", exported_at: new Date().toISOString(), languages: ["fr", "en"], content: {} };
    for (const page of Object.keys(DEFAULTS)) {
      payload.content[page] = { _label: PAGE_LABELS[page] || page, fields: {} };
      for (const key of Object.keys(DEFAULTS[page])) {
        const cur = (text[page] && text[page][key]) || {};
        payload.content[page].fields[key] = { fr: cur.fr ?? "", en: cur.en ?? "", modified: isModified(page, key) };
      }
    }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agrivoire-contenu-fr-en-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Contenu exporté (FR/EN).");
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    try {
      const parsed = JSON.parse(await file.text());
      const incoming = parsed?.content;
      if (!incoming || typeof incoming !== "object") {
        toast.error("Fichier invalide : structure 'content' introuvable.");
        return;
      }
      let applied = 0;
      setText((prev) => {
        const next = clone(prev);
        for (const page of Object.keys(DEFAULTS)) {
          const fields = incoming[page]?.fields;
          if (!fields) continue;
          for (const key of Object.keys(DEFAULTS[page])) {
            const f = fields[key];
            if (!f) continue;
            const fr = typeof f.fr === "string" ? f.fr : next[page][key].fr;
            const en = typeof f.en === "string" ? f.en : next[page][key].en;
            if (fr !== next[page][key].fr || en !== next[page][key].en) applied++;
            next[page][key] = { fr, en };
          }
        }
        return next;
      });
      toast.success(`Contenu importé (${applied} champ(s) mis à jour). Vérifiez puis cliquez sur Enregistrer.`);
    } catch (err) {
      toast.error("Impossible de lire le fichier (JSON invalide).");
    }
  };



  const save = async () => {
    setSaving(true);
    try {
      const data = diffContent(text, DEFAULTS);
      const mediaDiff = diffMedia(mediaMap, MEDIA_DEFAULTS);
      if (Object.keys(mediaDiff).length) data.media = mediaDiff;
      await api.put("/content", { data });
      await refreshContent();
      await loadSnapshots();
      toast.success("Contenu enregistré.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Média téléchargé.");
      await loadLibrary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du téléchargement.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteMedia = async (id) => {
    try { await api.delete(`/media/${id}`); await loadLibrary(); toast.success("Média supprimé."); }
    catch { toast.error("Erreur de suppression."); }
  };

  const assignSlot = (slot, url) => setMediaMap((prev) => ({ ...prev, [slot]: url }));
  const resetSlot = (slot) => setMediaMap((prev) => ({ ...prev, [slot]: MEDIA_DEFAULTS[slot] || "" }));

  const images = useMemo(() => library.filter((x) => x.kind === "image"), [library]);
  const videos = useMemo(() => library.filter((x) => x.kind === "video"), [library]);

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-neutral-300 border-t-[#4E7D2C]" />
      </div>
    );
  }

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-poppins text-sm font-semibold transition-colors ${tab === id ? "bg-neutral-900 text-white" : "bg-white text-neutral-600 hover:bg-neutral-200"}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-black/5 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <span className="font-montserrat text-lg font-black">
              <span className="text-[#F5821F]">Agrivoire</span> <span className="text-[#1CADE4]">Export</span>
            </span>
            <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-poppins text-xs text-neutral-500">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="hidden items-center gap-1.5 font-poppins text-sm text-neutral-500 hover:text-neutral-900 sm:flex" data-testid="admin-view-site">
              <ExternalLink className="h-4 w-4" /> Voir le site
            </Link>
            {user?.email && <span className="hidden font-poppins text-sm text-neutral-500 md:inline">{user.email}</span>}
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-full border border-black/10 px-4 py-2 font-poppins text-sm font-medium text-neutral-700 hover:bg-neutral-50">
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2">
            <TabBtn id="content" icon={FileText} label="Contenu" />
            <TabBtn id="media" icon={ImageIcon} label="Médias" />
            <TabBtn id="submissions" icon={Film} label="Soumissions" />
            <TabBtn id="history" icon={History} label="Historique" />
            <TabBtn id="security" icon={ShieldAlert} label="Sécurité" />
            <TabBtn id="settings" icon={Settings} label="Paramètres" />
          </div>
          {(tab === "content" || tab === "media") && (
            <div className="flex flex-wrap items-center gap-3">
              {tab === "content" && (
                <>
                  <input ref={importRef} type="file" accept="application/json,.json" onChange={onImportFile} className="hidden" data-testid="admin-import-input" />
                  <button onClick={() => importRef.current?.click()} data-testid="admin-import" className="flex items-center gap-2 rounded-full border border-black/10 px-5 py-2.5 font-poppins text-sm font-semibold text-neutral-700 transition-colors hover:border-[#8CC63F] hover:text-[#4E7D2C]">
                    <Upload className="h-4 w-4" /> Importer le contenu
                  </button>
                  <button onClick={exportContent} data-testid="admin-export" className="flex items-center gap-2 rounded-full border border-black/10 px-5 py-2.5 font-poppins text-sm font-semibold text-neutral-700 transition-colors hover:border-[#1CADE4] hover:text-[#1CADE4]">
                    <Download className="h-4 w-4" /> Exporter le contenu (FR/EN)
                  </button>
                </>
              )}
              <button onClick={save} disabled={saving} data-testid="admin-save" className="flex items-center gap-2 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-6 py-2.5 font-poppins text-sm font-semibold text-white shadow-sm transition-transform hover:scale-105 disabled:opacity-60">
                <Save className="h-4 w-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          )}
        </div>

        {/* CONTENT TAB */}
        {tab === "content" && (
          <div className="space-y-4" data-testid="content-editor">
            {Object.keys(DEFAULTS).map((page) => (
              <div key={page} className="overflow-hidden rounded-2xl border border-black/5 bg-white">
                <div className="flex items-center justify-between px-6 py-4">
                  <button
                    onClick={() => setOpenPage(openPage === page ? "" : page)}
                    data-testid={`content-page-${page}`}
                    className="flex flex-1 items-center gap-3 text-left"
                  >
                    <span className="font-montserrat text-base font-bold text-neutral-900">{PAGE_LABELS[page]}</span>
                    {pageModifiedCount(page) > 0 && (
                      <span data-testid={`page-modified-count-${page}`} className="rounded-full bg-[#F5821F]/12 px-2.5 py-0.5 font-poppins text-xs font-semibold text-[#F5821F]">
                        {pageModifiedCount(page)} modifié{pageModifiedCount(page) > 1 ? "s" : ""}
                      </span>
                    )}
                  </button>
                  <div className="flex items-center gap-3">
                    {pageModifiedCount(page) > 0 && (
                      <button
                        onClick={() => resetPage(page)}
                        data-testid={`reset-page-${page}`}
                        title="Réinitialiser toute la page au texte par défaut"
                        className="flex items-center gap-1.5 rounded-full border border-black/10 px-3 py-1.5 font-poppins text-xs font-medium text-neutral-500 transition-colors hover:border-[#F5821F] hover:text-[#F5821F]"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Tout réinitialiser cette page
                      </button>
                    )}
                    <button
                      onClick={() => setOpenPage(openPage === page ? "" : page)}
                      className="font-poppins text-lg text-neutral-400"
                      aria-label="Ouvrir/fermer"
                    >
                      {openPage === page ? "−" : "+"}
                    </button>
                  </div>
                </div>
                {openPage === page && (
                  <div className="space-y-5 border-t border-black/5 px-6 py-6">
                    {Object.keys(DEFAULTS[page]).map((key) => (
                      <div key={key} className="grid gap-3 md:grid-cols-2">
                        <div className="flex items-center justify-between gap-3 md:col-span-2">
                          <span className="flex flex-wrap items-center gap-2">
                            <span className="font-poppins text-sm font-semibold text-neutral-700">{FIELD_LABELS[`${page}.${key}`] || prettifyKey(key)}</span>
                            <span className="font-mono text-[10px] text-neutral-400">{key}</span>
                            {isModified(page, key) && (
                              <span data-testid={`modified-badge-${page}-${key}`} className="rounded-full bg-[#F5821F]/12 px-2 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide text-[#F5821F]">
                                Modifié
                              </span>
                            )}
                          </span>
                          {isModified(page, key) && (
                            <button
                              onClick={() => resetField(page, key)}
                              data-testid={`reset-${page}-${key}`}
                              title="Rétablir le texte par défaut"
                              className="flex items-center gap-1 rounded-full border border-black/10 px-2.5 py-1 font-poppins text-[11px] font-medium text-neutral-500 transition-colors hover:border-[#F5821F] hover:text-[#F5821F]"
                            >
                              <RotateCcw className="h-3 w-3" /> Réinitialiser
                            </button>
                          )}
                        </div>
                        <div>
                          <label className="font-poppins text-xs font-medium text-[#4E7D2C]">Français</label>
                          <textarea
                            rows={2}
                            data-testid={`field-${page}-${key}-fr`}
                            value={text[page]?.[key]?.fr ?? ""}
                            onChange={(e) => setField(page, key, "fr", e.target.value)}
                            className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2 font-poppins text-sm outline-none focus:border-[#4E7D2C]"
                          />
                        </div>
                        <div>
                          <label className="font-poppins text-xs font-medium text-[#1CADE4]">English</label>
                          <textarea
                            rows={2}
                            data-testid={`field-${page}-${key}-en`}
                            value={text[page]?.[key]?.en ?? ""}
                            onChange={(e) => setField(page, key, "en", e.target.value)}
                            className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2 font-poppins text-sm outline-none focus:border-[#1CADE4]"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* MEDIA TAB */}
        {tab === "media" && (
          <div className="space-y-8" data-testid="media-manager">
            <div className="rounded-2xl border border-black/5 bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h3 className="font-montserrat text-lg font-bold">Bibliothèque de médias</h3>
                  <p className="font-poppins text-sm text-neutral-500">Images (JPG, PNG, WebP) et vidéos (MP4). Cliquez « Enregistrer » après avoir assigné un média.</p>
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm" onChange={onUpload} className="hidden" data-testid="media-upload-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-btn" className="flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 font-poppins text-sm font-semibold text-white disabled:opacity-60">
                    <Upload className="h-4 w-4" /> {uploading ? "Téléchargement..." : "Téléverser un média"}
                  </button>
                </div>
              </div>

              {library.length > 0 && (
                <div className="mt-6 grid gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {library.map((it) => (
                    <div key={it.id} className="group relative overflow-hidden rounded-xl border border-black/5 bg-neutral-50" data-testid={`library-item-${it.id}`}>
                      {it.kind === "image" ? (
                        <img src={resolveMedia(it.url)} alt={it.original_filename} className="aspect-video w-full object-cover" />
                      ) : (
                        <video src={resolveMedia(it.url)} muted className="aspect-video w-full object-cover" />
                      )}
                      <div className="flex items-center justify-between p-2">
                        <span className="truncate font-poppins text-xs text-neutral-500">{it.original_filename}</span>
                        <button onClick={() => deleteMedia(it.id)} data-testid={`delete-media-${it.id}`} className="text-neutral-400 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <SlotSection title="Images du site" slots={IMAGE_SLOTS} options={images} mediaMap={mediaMap} assignSlot={assignSlot} resetSlot={resetSlot} kind="image" />
            <SlotSection title="Vidéos (Courtage : filières & process)" slots={VIDEO_SLOTS} options={videos} mediaMap={mediaMap} assignSlot={assignSlot} resetSlot={resetSlot} kind="video" />
          </div>
        )}

        {/* SUBMISSIONS TAB */}
        {tab === "submissions" && (
          <div className="rounded-2xl border border-black/5 bg-white p-6" data-testid="submissions-view">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-montserrat text-lg font-bold">Soumissions ({submissions.length})</h3>
              <button onClick={loadSubmissions} className="flex items-center gap-1.5 font-poppins text-sm text-neutral-500 hover:text-neutral-900">
                <RefreshCw className="h-4 w-4" /> Actualiser
              </button>
            </div>
            {submissions.length === 0 ? (
              <p className="font-poppins text-sm text-neutral-400">Aucune soumission pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {submissions.map((s) => (
                  <div key={s.id} className="rounded-xl border border-black/5 p-4" data-testid={`submission-${s.id}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 font-poppins text-xs font-semibold ${s.type === "buyer" ? "bg-[#8CC63F]/15 text-[#4E7D2C]" : "bg-[#1CADE4]/15 text-[#0f7ba3]"}`}>
                        {s.type === "buyer" ? "Acheteur/Fournisseur" : "Conseil"}
                      </span>
                      <span className="font-montserrat text-sm font-bold">{s.full_name} — {s.company}</span>
                      <span className="font-poppins text-xs text-neutral-400">{s.country} · {s.email}</span>
                      {s.email_delivered ? (
                        <span className="ml-auto flex items-center gap-1 font-poppins text-xs text-green-600"><Check className="h-3 w-3" /> Email envoyé</span>
                      ) : (
                        <span className="ml-auto font-poppins text-xs text-amber-600">Sauvegardé (email non envoyé)</span>
                      )}
                    </div>
                    <p className="mt-2 font-poppins text-sm text-neutral-600">
                      {s.type === "buyer" ? `${s.role} · ${(s.products || []).join(", ")} · ${s.message}` : `${s.mission_type} · ${s.description}`}
                    </p>
                    <p className="mt-1 font-poppins text-xs text-neutral-400">{new Date(s.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === "history" && (
          <div className="max-w-3xl space-y-6" data-testid="history-view">
            <div className="rounded-2xl border border-black/5 bg-white p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#1CADE4]/10 text-[#1CADE4]">
                  <History className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-montserrat text-lg font-bold">Historique des versions</h3>
                  <p className="font-poppins text-sm text-neutral-500">
                    Une sauvegarde est créée automatiquement à chaque « Enregistrer ». Vous pouvez aussi en créer une manuellement, puis restaurer n'importe quelle version en cas d'erreur (ex. après un import).
                  </p>
                </div>
              </div>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={snapshotLabel}
                  onChange={(e) => setSnapshotLabel(e.target.value)}
                  data-testid="snapshot-label-input"
                  placeholder="Nom de la sauvegarde (optionnel), ex. « Version finale FR/EN »"
                  className="flex-1 rounded-xl border border-black/10 px-4 py-3 font-poppins text-sm outline-none focus:border-[#1CADE4]"
                />
                <button
                  onClick={createSnapshot}
                  disabled={snapBusy}
                  data-testid="snapshot-create-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-6 py-3 font-poppins text-sm font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" /> Créer une sauvegarde
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-black/5 bg-white p-6" data-testid="snapshots-list">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="font-montserrat text-base font-bold">Versions enregistrées ({snapshots.length})</h3>
                <button onClick={loadSnapshots} className="flex items-center gap-1.5 font-poppins text-sm text-neutral-500 hover:text-neutral-900">
                  <RefreshCw className="h-4 w-4" /> Actualiser
                </button>
              </div>
              {snapshots.length === 0 ? (
                <p className="font-poppins text-sm text-neutral-400">Aucune sauvegarde pour le moment. Enregistrez du contenu pour créer la première.</p>
              ) : (
                <div className="space-y-3">
                  {snapshots.map((s) => (
                    <div key={s.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-black/5 p-4" data-testid={`snapshot-${s.id}`}>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-montserrat text-sm font-bold text-neutral-900">
                            {new Date(s.created_at).toLocaleString()}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide ${s.kind === "manual" ? "bg-[#1CADE4]/12 text-[#1CADE4]" : "bg-neutral-100 text-neutral-500"}`}>
                            {s.kind === "manual" ? "Manuelle" : "Auto"}
                          </span>
                          <span className="font-poppins text-xs text-neutral-400">{s.field_count} champ(s) personnalisé(s)</span>
                        </div>
                        {s.label && <p className="mt-1 truncate font-poppins text-sm text-neutral-600">{s.label}</p>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => restoreSnapshot(s)}
                          disabled={snapBusy}
                          data-testid={`snapshot-restore-${s.id}`}
                          className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-4 py-2 font-poppins text-xs font-semibold text-white transition-transform hover:scale-105 disabled:opacity-60"
                        >
                          <Undo2 className="h-3.5 w-3.5" /> Restaurer
                        </button>
                        <button
                          onClick={() => deleteSnapshot(s.id)}
                          data-testid={`snapshot-delete-${s.id}`}
                          title="Supprimer cette sauvegarde"
                          className="rounded-full border border-black/10 p-2 text-neutral-400 transition-colors hover:border-red-400 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SECURITY TAB */}
        {tab === "security" && (
          <div className="space-y-6" data-testid="security-view">
            <div className="rounded-2xl border border-black/5 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                    <ShieldAlert className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="font-montserrat text-lg font-bold">Événements de sécurité</h3>
                    <p className="font-poppins text-sm text-neutral-500">
                      Connexions refusées, tentatives répétées, limites de formulaire atteintes et spams bloqués (honeypot). Conservés 30 jours.
                    </p>
                  </div>
                </div>
                <button onClick={loadSecurityEvents} className="flex items-center gap-1.5 font-poppins text-sm text-neutral-500 hover:text-neutral-900" data-testid="security-refresh">
                  <RefreshCw className="h-4 w-4" /> Actualiser
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-black/5 bg-white p-6" data-testid="security-events-list">
              <h3 className="mb-4 font-montserrat text-base font-bold">Journal ({securityEvents.length})</h3>
              {securityEvents.length === 0 ? (
                <p className="font-poppins text-sm text-neutral-400">Aucun événement de sécurité récent. C'est bon signe !</p>
              ) : (
                <div className="space-y-2">
                  {securityEvents.map((ev, i) => {
                    const meta = SEC_EVENT_META[ev.event] || { label: ev.event, tone: "neutral" };
                    return (
                      <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-black/5 px-4 py-3" data-testid={`security-event-${i}`}>
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <span className={`rounded-full px-2.5 py-0.5 font-poppins text-[11px] font-bold uppercase tracking-wide ${SEC_TONE[meta.tone]}`}>
                            {meta.label}
                          </span>
                          {ev.ip && <span className="font-mono text-xs text-neutral-500">IP {ev.ip}</span>}
                          {(ev.city || ev.country) && (
                            <span className="rounded-full bg-neutral-100 px-2 py-0.5 font-poppins text-[11px] text-neutral-600" data-testid={`security-event-${i}-location`}>
                              {[ev.city, ev.country].filter(Boolean).join(", ")}
                            </span>
                          )}
                          {ev.detail && <span className="truncate font-poppins text-xs text-neutral-400">{ev.detail}</span>}
                        </div>
                        <span className="font-poppins text-xs text-neutral-400">{new Date(ev.created_at).toLocaleString()}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SETTINGS TAB */}
        {tab === "settings" && (
          <div className="max-w-2xl space-y-6" data-testid="settings-view">
            <div className="rounded-2xl border border-black/5 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#8CC63F]/10 text-[#4E7D2C]">
                  <Mail className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-montserrat text-lg font-bold">Email de réception des formulaires</h3>
                  <p className="font-poppins text-sm text-neutral-500">Les demandes des deux formulaires (Acheteur/Fournisseur et Partenaire/Conseil) seront envoyées à cette adresse.</p>
                </div>
              </div>
              <label className="mt-6 block font-poppins text-xs font-medium text-neutral-500">Adresse email de réception</label>
              <div className="mt-1.5 flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  data-testid="settings-recipient-email"
                  placeholder="[email protected]"
                  className="flex-1 rounded-xl border border-black/10 px-4 py-3 font-poppins text-sm outline-none focus:border-[#4E7D2C]"
                />
                <button
                  onClick={saveSettings}
                  disabled={savingSettings || !recipientEmail.trim()}
                  data-testid="settings-save"
                  className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#8CC63F] to-[#4E7D2C] px-6 py-3 font-poppins text-sm font-semibold text-white shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" /> {savingSettings ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-red-100 bg-white p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-50 text-red-600">
                  <ShieldAlert className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="font-montserrat text-lg font-bold">Email d'alerte de sécurité</h3>
                  <p className="font-poppins text-sm text-neutral-500">Recevez un email uniquement en cas d'activité vraiment suspecte (tentatives de connexion admin répétées, pic anormal de formulaires). Alertes limitées pour éviter le spam. Vide = utilise l'email de réception ci-dessus.</p>
                </div>
              </div>
              <label className="mt-6 block font-poppins text-xs font-medium text-neutral-500">Adresse email d'alerte</label>
              <div className="mt-1.5 flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  data-testid="settings-alert-email"
                  placeholder="[email protected]"
                  className="flex-1 rounded-xl border border-black/10 px-4 py-3 font-poppins text-sm outline-none focus:border-red-400"
                />
                <button
                  onClick={saveAlertEmail}
                  disabled={savingAlert || !alertEmail.trim()}
                  data-testid="settings-alert-save"
                  className="flex items-center justify-center gap-2 rounded-full bg-red-600 px-6 py-3 font-poppins text-sm font-semibold text-white shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
                >
                  <Save className="h-4 w-4" /> {savingAlert ? "Enregistrement..." : "Enregistrer"}
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-[#1CADE4]/20 bg-[#1CADE4]/5 p-6">
              <h3 className="font-montserrat text-base font-bold text-neutral-900">Email de contact affiché sur le site</h3>
              <p className="mt-2 font-poppins text-sm text-neutral-600">
                L'adresse email visible publiquement (pied de page + page Contact) se modifie dans l'onglet
                <span className="font-semibold"> Contenu → Global → email</span>, puis cliquez sur « Enregistrer ».
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function SlotSection({ title, slots, options, mediaMap, assignSlot, resetSlot, kind }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-6">
      <h3 className="font-montserrat text-lg font-bold">{title}</h3>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        {slots.map(({ slot, label }) => {
          const cur = mediaMap[slot];
          return (
            <div key={slot} className="rounded-xl border border-black/5 p-4" data-testid={`slot-${slot}`}>
              <p className="font-poppins text-sm font-semibold text-neutral-800">{label}</p>
              <div className="mt-3 aspect-video overflow-hidden rounded-lg bg-neutral-100">
                {cur ? (
                  kind === "image" ? (
                    <img src={resolveMedia(cur)} alt={label} className="h-full w-full object-cover" />
                  ) : (
                    <video src={resolveMedia(cur)} muted className="h-full w-full object-cover" />
                  )
                ) : (
                  <div className="flex h-full items-center justify-center font-poppins text-xs text-neutral-400">Aucun média</div>
                )}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <select
                  value={options.some((o) => o.url === cur) ? cur : ""}
                  onChange={(e) => assignSlot(slot, e.target.value)}
                  data-testid={`slot-select-${slot}`}
                  className="flex-1 rounded-lg border border-black/10 px-2 py-2 font-poppins text-xs outline-none focus:border-[#4E7D2C]"
                >
                  <option value="">{kind === "video" ? "— Aucune vidéo —" : "— Choisir un média —"}</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.url}>{o.original_filename}</option>
                  ))}
                </select>
                <button onClick={() => resetSlot(slot)} data-testid={`slot-reset-${slot}`} className="rounded-lg border border-black/10 px-3 py-2 font-poppins text-xs text-neutral-500 hover:bg-neutral-50">
                  Défaut
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
